/**
 * The welcome coach mark: three short spotlight steps on the Marketing chat, opened once when
 * onboarding finishes (`?tour=welcome`) and again from the account menu's "Take a tour". It
 * points at where approvals arrive, at Work (what Marketing is doing, and what needs you), and
 * at the credits balance.
 *
 * Anchors are literal `data-tour="…"` attributes: the chat thread (ChatSurface), the Work
 * button and the credits balance (HeaderActions), all visible on phones too.
 *
 * Copy rule: a step may only describe a control the user can actually reach.
 * `welcome.test.ts` enforces the step order and a denylist of claims that
 * have previously drifted out of the UI.
 */
import { CHAT_THREAD_PATH } from '@/hooks/useIsChatHome'
import type { TourDefinition } from './types'

export const WELCOME_TOUR_ID = 'welcome'

/** The section sequence, in order — one topic per step, pinned by the guard test. */
export const WELCOME_TOUR_SECTIONS = ['Approvals', 'Work', 'Credits'] as const

export const welcomeTour: TourDefinition = {
  id: WELCOME_TOUR_ID,
  title: 'Welcome to Maxflow',
  steps: [
    {
      id: 'chat-approvals',
      route: CHAT_THREAD_PATH,
      target: 'chat-thread',
      section: 'Approvals',
      title: 'Your OK comes here',
      body: 'Before Marketing posts, sends or spends anything, a card shows up in this chat. One tap approves it, or tell Marketing what it may do on its own.',
    },
    {
      id: 'work',
      route: CHAT_THREAD_PATH,
      target: 'work-button',
      section: 'Work',
      title: 'Work and Needs you',
      body: 'Work shows what Marketing is doing right now, with anything waiting on you first, under Needs you. Planned posts are on the Calendar.',
    },
    {
      id: 'credits',
      route: CHAT_THREAD_PATH,
      target: 'credit-balance',
      section: 'Credits',
      title: 'Your credits',
      body: 'Making posts and videos uses credits. Your balance is always here, and ads or other spending wait for your OK unless you say otherwise.',
    },
  ],
}
